<template>
    <div class="detail-panel">
        <div v-if="!product && !isAdding" class="detail-empty">
            <div class="empty-inner">
                <i class="fas fa-cube empty-icon"></i>
                <p class="empty-title">상품을 선택하세요</p>
            </div>
        </div>

        <div v-else class="detail-form-wrap">
            <div class="detail-body">
                <section class="detail-section">
                    <div class="detail-section-head">
                        <span class="detail-section-title">기본 정보</span>
                    </div>

                    <div class="detail-basic-grid">
                        <div class="field-row">
                            <label class="field-label">상품코드 <span class="req">*</span></label>
                            <input
                                v-model.trim="form.product_code"
                                class="field-input"
                                :class="{
                                    'field-input--error': errors.product_code,
                                    'field-input--disabled': !canEdit,
                                }"
                                placeholder="예: PRD-001"
                                :disabled="!canEdit"
                            />
                            <p v-if="errors.product_code" class="field-error">{{ errors.product_code }}</p>
                        </div>

                        <div class="field-row">
                            <label class="field-label">상품명 <span class="req">*</span></label>
                            <input
                                v-model.trim="form.product_name"
                                class="field-input"
                                :class="{
                                    'field-input--error': errors.product_name,
                                    'field-input--disabled': !canEdit,
                                }"
                                placeholder="상품명 입력"
                                :disabled="!canEdit"
                            />
                            <p v-if="errors.product_name" class="field-error">{{ errors.product_name }}</p>
                        </div>

                        <!-- 목표 원단위 입력 비활성화 -->
                        <!--
                        <div class="field-row">
                            <label class="field-label">목표 원단위</label>
                            <input
                                v-model="form.target_ei"
                                class="field-input"
                                type="number"
                                step="any"
                                placeholder="숫자"
                                :class="{ 'field-input--disabled': !canEdit }"
                                :disabled="!canEdit"
                            />
                        </div>

                        <div class="field-row">
                            <label class="field-label">목표 단위</label>
                            <input
                                v-model.trim="form.target_ei_unit"
                                class="field-input"
                                placeholder="예: MJ/kg"
                                :class="{ 'field-input--disabled': !canEdit }"
                                :disabled="!canEdit"
                            />
                        </div>
                        -->
                    </div>

                    <div class="field-row field-row--remarks">
                        <label class="field-label">비고</label>
                        <textarea
                            v-model.trim="form.remarks"
                            class="field-textarea field-textarea--fixed"
                            :class="{ 'field-input--disabled': !canEdit }"
                            placeholder="메모·설명"
                            :disabled="!canEdit"
                        ></textarea>
                    </div>
                </section>

                <section class="detail-section">
                    <div class="detail-section-head">
                        <span class="detail-section-title">생산 단위</span>
                    </div>

                    <div class="field-row">
                        <div class="unit-grid" :class="{ 'unit-grid--disabled': !canEdit }">
                            <button
                                v-for="unit in units"
                                :key="unit.value"
                                type="button"
                                class="unit-chip"
                                :class="{ 'unit-chip--on': form.product_unit === unit.value }"
                                :disabled="!canEdit"
                                @click="canEdit && (form.product_unit = unit.value)"
                            >
                                <b>{{ unit.value }}</b>
                                <small>{{ unit.sub }}</small>
                            </button>
                        </div>
                        <p v-if="errors.product_unit" class="field-error">{{ errors.product_unit }}</p>
                    </div>
                </section>

                <section class="detail-section">
                    <div class="detail-section-head">
                        <span class="detail-section-title">관제점 연결</span>
                    </div>

                    <div class="field-row">
                        <div class="pt-selector" :class="{ 'pt-selector--disabled': !canEdit }">
                            <div class="pt-display" @click="canEdit && openPointSearch()">
                                <i
                                    class="fas fa-link pt-icon"
                                    :class="form.pt_addr ? 'pt-icon--linked' : 'pt-icon--empty'"
                                ></i>
                                <span class="pt-copy">
                                    <span class="pt-addr" :class="{ 'pt-addr--empty': !form.pt_addr }">
                                        {{ form.pt_addr || "관제점을 선택하세요" }}
                                    </span>
                                </span>
                                <button
                                    v-if="form.pt_addr && canEdit"
                                    type="button"
                                    class="pt-clear"
                                    title="연결 해제"
                                    @click.stop="clearLinkedPoint()"
                                >
                                    <i class="fas fa-times"></i>
                                </button>
                                <i v-if="canEdit" class="fas fa-search pt-search-ico"></i>
                            </div>
                        </div>
                    </div>
                </section>

                <section class="detail-section detail-section--energy">
                    <div class="detail-section-head">
                        <span class="detail-section-title">에너지 집계 매핑</span>
                    </div>
                    <p v-if="!energyMapPtAddr" class="detail-energy-hint">
                        생산 관제점을 연결하면 원단위 산출 시 합산할 에너지(전력 등) 관제점을 지정할 수 있습니다.
                    </p>
                    <template v-else>
                        <p class="detail-energy-desc">
                            상품 저장 시 에너지 매핑이 함께 반영되며, 스케줄 원단위 산출에 사용됩니다.
                        </p>
                        <div class="detail-energy-row">
                            <ul v-if="energyPtAddrs.length" class="detail-energy-chips">
                                <li
                                    v-for="(addr, ix) in energyPtAddrs"
                                    :key="`emap-${ix}-${addr}`"
                                    class="detail-energy-chip"
                                >
                                    <span class="detail-energy-chip-txt">{{ addr }}</span>
                                    <button
                                        type="button"
                                        class="detail-energy-chip-x"
                                        title="제거"
                                        @click="removeEnergyAddr(ix)"
                                    >
                                        <i class="fas fa-times"></i>
                                    </button>
                                </li>
                            </ul>
                            <p v-else class="detail-energy-empty detail-energy-empty--inline">
                                등록된 에너지 관제점이 없습니다.
                            </p>
                            <button
                                type="button"
                                class="detail-energy-add"
                                :disabled="energyMapLoading"
                                @click="openEnergyPointModal"
                            >
                                <i class="fas fa-plus"></i>
                                에너지 관제점 추가
                            </button>
                        </div>
                    </template>
                </section>

                <section class="detail-section detail-section--status">
                    <div class="detail-section-head">
                        <span class="detail-section-title">운영 상태</span>
                    </div>

                    <div class="field-row">
                        <div class="yn-toggle" :class="{ 'yn-toggle--disabled': !canEdit }">
                            <button
                                type="button"
                                class="yn-btn"
                                :class="{ 'yn-btn--on': form.use_yn === 'Y' }"
                                :disabled="!canEdit"
                                @click="canEdit && (form.use_yn = 'Y')"
                            >
                                사용
                            </button>
                            <button
                                type="button"
                                class="yn-btn"
                                :class="{ 'yn-btn--off': form.use_yn === 'N' }"
                                :disabled="!canEdit"
                                @click="canEdit && (form.use_yn = 'N')"
                            >
                                미사용
                            </button>
                        </div>
                    </div>
                </section>
            </div>
        </div>

        <PointSearchModal
            :visible.sync="showPointModal"
            :keyword.sync="ptKeyword"
            :loading="ptLoading"
            :rows="ptResults"
            :list-total-count="ptListTotalCount"
            title="생산 관제점 검색"
            subtitle="PRODUCT 그룹만"
            empty-no-data-message="PRODUCT·PROD 그룹 관제점이 없습니다."
            empty-filtered-message="검색 결과가 없습니다."
            @search="searchPoints"
            @select="selectPoint"
            @close="closePointModal"
        />

        <PointSearchModal
            :visible.sync="showEnergyPtModal"
            :keyword.sync="energyPtKeyword"
            :loading="energyPtLoading"
            :rows="energyPtResults"
            :list-total-count="energyListTotalCount"
            title="에너지 관제점 검색"
            subtitle="에너지 관제점만 표시"
            title-icon-class="fas fa-bolt"
            search-placeholder="주소·이름·그룹으로 필터"
            empty-no-data-message="건물에너지(Bep) 관제점이 없습니다."
            empty-filtered-message="검색 결과가 없습니다."
            loading-label="불러오는 중"
            :columns="energyPointTableColumns"
            @search="searchEnergyPoints"
            @select="selectEnergyPoint"
            @close="closeEnergyPointModal"
        />
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import { PRODUCT_UNITS } from "../config/units.js";
import PointSearchModal from "./PointSearchModal.vue";

export default {
    name: "ProductDetailPanel",
    components: { PointSearchModal },
    props: {
        product: { type: Object, default: null },
        isAdding: { type: Boolean, default: false },
        allProducts: { type: Array, default: () => [] },
    },
    data() {
        return {
            isEditing: false,
            form: this.makeForm(null),
            errors: {},
            units: PRODUCT_UNITS.map((unit) => ({
                value: unit.value,
                sub: unit.label.match(/\((.+)\)/)?.[1] || unit.value,
            })),
            showPointModal: false,
            ptKeyword: "",
            ptResults: [],
            /** 모달에 로드한 전체 관제점 (xeUpl totalhintIncludeUPLfunc 의 pointList 출처와 동일: FETCH_POINT_LIST) */
            ptAllPoints: [],
            ptLoading: false,
            energyPtAddrs: [],
            energyMapLoading: false,
            showEnergyPtModal: false,
            energyPtKeyword: "",
            energyPtAllPoints: [],
            energyPtResults: [],
            energyPtLoading: false,
            energyPointTableColumns: [
                { field: "ptAddr", label: "주소", emphasis: true },
                { field: "ptName", label: "이름" },
                { field: "ptGroup", label: "그룹" },
            ],
        };
    },
    computed: {
        ptListTotalCount() {
            return this.ptAllPoints.length;
        },
        energyListTotalCount() {
            return this.energyPtAllPoints.length;
        },
        energyMapPtAddr() {
            return String(this.form.pt_addr || "").trim();
        },
        canEdit() {
            return this.isAdding || this.isEditing;
        },
    },
    watch: {
        product(value) {
            this.isEditing = false;
            this.errors = {};
            this.form = this.makeForm(value);
            this.energyPtAddrs = [];
            this.showPointModal = false;
            this.showEnergyPtModal = false;
            this.ptKeyword = "";
            this.ptResults = [];
            this.ptLoading = false;
            this.energyPtKeyword = "";
            this.energyPtResults = [];
            this.energyPtLoading = false;
            this.$nextTick(() => this.loadEnergyMapFromServer());
        },
        "form.pt_addr"() {
            this.$nextTick(() => this.loadEnergyMapFromServer());
        },
        isAdding(value) {
            if (!value) return;
            this.isEditing = false;
            this.errors = {};
            this.form = this.makeForm(null);
            this.energyPtAddrs = [];
        },
    },
    methods: {
        makeForm(product) {
            if (!product) {
                return {
                    product_id: null,
                    product_code: "",
                    product_name: "",
                    product_unit: "EA",
                    pt_addr: "",
                    default_prod_pt_idx: null,
                    use_yn: "Y",
                    remarks: "",
                    target_ei: null,
                    target_ei_unit: "",
                    created_at: "",
                    updated_at: "",
                };
            }
            return {
                ...product,
                remarks: product.remarks != null ? String(product.remarks) : "",
                target_ei: product.target_ei != null && product.target_ei !== "" ? product.target_ei : null,
                target_ei_unit: product.target_ei_unit != null ? String(product.target_ei_unit) : "",
                default_prod_pt_idx: product.default_prod_pt_idx != null ? product.default_prod_pt_idx : null,
            };
        },
        startEdit() {
            this.isEditing = true;
        },
        /** 모달 닫힘 후 폼 초기화 (부모 b-modal @hidden) */
        resetAfterAddModalClose() {
            this.errors = {};
            this.isEditing = false;
            this.form = this.makeForm(null);
            this.energyPtAddrs = [];
        },
        resetAfterEditModalClose() {
            this.errors = {};
            this.isEditing = false;
            this.form = this.makeForm(this.product);
        },
        validate() {
            const errors = {};
            const productCode = this.form.product_code.trim();
            const productName = this.form.product_name.trim();
            const duplicated = this.allProducts.find(
                (product) => product.product_code === productCode && product.product_id !== this.form.product_id,
            );

            if (!productCode) errors.product_code = "상품코드를 입력하세요.";
            else if (duplicated) errors.product_code = "이미 존재하는 상품코드입니다.";

            if (!productName) errors.product_name = "상품명을 입력하세요.";
            if (!this.form.product_unit) errors.product_unit = "단위를 선택하세요.";

            this.errors = errors;
            return !Object.keys(errors).length;
        },
        /**
         * 검증 후 저장 페이로드 (에너지 매핑 주소 목록 포함). 실패 시 null.
         * 부모 모달 OK에서 상품 API + 에너지 매핑 일괄 저장에 사용.
         */
        getSavePayload() {
            if (!this.validate()) return null;
            const now = new Date().toLocaleString("sv-SE");
            const payload = {
                ...this.form,
                product_code: this.form.product_code.trim(),
                product_name: this.form.product_name.trim(),
                energyPtAddrs: [...this.energyPtAddrs],
            };
            if (this.isAdding) {
                return { ...payload, created_at: now, updated_at: now };
            }
            return { ...payload, updated_at: now };
        },
        /**
         * 서버 productMasterService.resolveDefaultProdPoint 와 동일: PRODUCT / PROD 계열 그룹만 허용.
         */
        matchesProductPtGroup(ptGroup) {
            const g = String(ptGroup || "").toUpperCase();
            return g.includes("PRODUCT") || g.includes("PROD");
        },
        /**
         * xeUpl.vue totalhintIncludeUPLfunc: store.state.pointList 기반.
         * 목록은 pointInfo/getPointList — 화면에는 생산용(PRODUCT/PROD 그룹) 관제점만 사용.
         */
        mapStorePointRows(raw) {
            const list = Array.isArray(raw) ? raw : [];
            return list
                .filter((item) => this.matchesProductPtGroup(item.ptGroup))
                .map((item) => ({
                    ptIdx: item.ptIdx,
                    ptAddr: item.ptAddr,
                    ptName: item.ptName || "",
                    ptGroup: item.ptGroup || "",
                }));
        },
        applyPointListFilter() {
            const kw = this.ptKeyword.trim().toLowerCase();
            if (!kw) {
                this.ptResults = this.ptAllPoints.slice();
                return;
            }
            this.ptResults = this.ptAllPoints.filter(
                (p) =>
                    (p.ptAddr && p.ptAddr.toLowerCase().includes(kw)) ||
                    (p.ptName && p.ptName.toLowerCase().includes(kw)) ||
                    (p.ptGroup && p.ptGroup.toLowerCase().includes(kw)),
            );
        },
        async openPointSearch() {
            this.ptKeyword = "";
            this.ptResults = [];
            this.ptAllPoints = [];
            this.showPointModal = true;
            this.ptLoading = true;

            try {
                const result = await backEndApi.pointInfo.getPointList();
                if (!result || result.status !== 200 || !Array.isArray(result.data)) {
                    throw new Error("api");
                }
                this.$store.commit("SET_POINT_LIST", result.data);
                this.ptAllPoints = this.mapStorePointRows(result.data);
                this.applyPointListFilter();
            } catch {
                this.ptAllPoints = [];
                this.ptResults = [];
                this.alertNoti("관제점 목록을 불러오지 못했습니다.");
            } finally {
                this.ptLoading = false;
            }
        },
        closePointModal() {
            this.showPointModal = false;
        },
        searchPoints() {
            if (!this.ptAllPoints.length) {
                this.alertNoti("목록을 먼저 불러오세요.");
                return;
            }
            this.applyPointListFilter();
        },
        clearLinkedPoint() {
            this.form.pt_addr = "";
            this.form.default_prod_pt_idx = null;
        },
        selectPoint(point) {
            this.form.pt_addr = point.ptAddr;
            this.form.default_prod_pt_idx = point.ptIdx != null ? point.ptIdx : null;
            this.closePointModal();
        },
        /** 건물에너지 성능분석(Bep) 관제점만 에너지 집계 후보로 표시 */
        isBepPurpsUse(purpsUseCode) {
            return (
                String(purpsUseCode || "")
                    .trim()
                    .toLowerCase() === "bep"
            );
        },
        mapEnergyPointRows(raw) {
            const list = Array.isArray(raw) ? raw : [];
            return list
                .filter((item) => this.isBepPurpsUse(item.purpsUseCode))
                .map((item) => ({
                    ptIdx: item.ptIdx,
                    ptAddr: item.ptAddr,
                    ptName: item.ptName || "",
                    ptGroup: item.ptGroup || "",
                }));
        },
        async loadEnergyMapFromServer() {
            const addr = this.energyMapPtAddr;
            if (!addr) {
                this.energyPtAddrs = [];
                return;
            }
            this.energyMapLoading = true;
            try {
                const res = await backEndApi.peiProdEnergyMap.getProdEnergyMapByProdPt({ prodPtAddr: addr });
                if (res.status !== 200) throw new Error("api");
                const arr = Array.isArray(res.data) ? res.data : [];
                this.energyPtAddrs = arr
                    .map((row) => row.energyPtAddr || row.energyPtaddr)
                    .filter((x) => x != null && String(x).trim() !== "");
            } catch {
                this.energyPtAddrs = [];
                this.alertNoti("에너지 매핑을 불러오지 못했습니다.");
            } finally {
                this.energyMapLoading = false;
            }
        },
        removeEnergyAddr(index) {
            if (index < 0 || index >= this.energyPtAddrs.length) return;
            this.energyPtAddrs.splice(index, 1);
        },
        async openEnergyPointModal() {
            this.energyPtKeyword = "";
            this.showEnergyPtModal = true;
            this.energyPtAllPoints = [];
            this.energyPtResults = [];
            this.energyPtLoading = true;
            try {
                const res = await backEndApi.pointInfo.getPointList();
                if (!res || res.status !== 200 || !Array.isArray(res.data)) throw new Error("api");
                this.energyPtAllPoints = this.mapEnergyPointRows(res.data);
                this.applyEnergyPointFilter();
            } catch {
                this.energyPtAllPoints = [];
                this.energyPtResults = [];
                this.alertNoti("관제점 목록을 불러오지 못했습니다.");
            } finally {
                this.energyPtLoading = false;
            }
        },
        closeEnergyPointModal() {
            this.showEnergyPtModal = false;
        },
        applyEnergyPointFilter() {
            const kw = this.energyPtKeyword.trim().toLowerCase();
            if (!kw) {
                this.energyPtResults = this.energyPtAllPoints.slice();
                return;
            }
            this.energyPtResults = this.energyPtAllPoints.filter(
                (p) =>
                    (p.ptAddr && p.ptAddr.toLowerCase().includes(kw)) ||
                    (p.ptName && p.ptName.toLowerCase().includes(kw)) ||
                    (p.ptGroup && p.ptGroup.toLowerCase().includes(kw)),
            );
        },
        searchEnergyPoints() {
            if (!this.energyPtAllPoints.length) {
                this.alertNoti("목록을 먼저 불러오세요.");
                return;
            }
            this.applyEnergyPointFilter();
        },
        selectEnergyPoint(point) {
            const a = point && point.ptAddr != null ? String(point.ptAddr).trim() : "";
            if (!a) return;
            if (!this.energyPtAddrs.includes(a)) this.energyPtAddrs.push(a);
            this.closeEnergyPointModal();
        },
    },
};
</script>

<style scoped>
.detail-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0;
    background: linear-gradient(180deg, rgba(248, 250, 252, 0.76), rgba(255, 255, 255, 0.98));
    position: relative;
}

.detail-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
}

.empty-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    max-width: 220px;
    padding: 18px 14px;
    border-radius: 12px;
    border: 1px dashed rgba(203, 213, 225, 0.92);
    background: rgba(248, 250, 252, 0.78);
    text-align: center;
}

.empty-icon {
    font-size: 24px;
    color: var(--puc-slate-300, #cbd5e1);
}

.empty-title {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: var(--puc-slate-700, #475569);
    line-height: 1.4;
}

.detail-form-wrap {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
}

.detail-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px;
}

.overview-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 22px;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
}

.overview-badge--unit {
    background: rgba(15, 23, 42, 0.06);
    color: var(--puc-slate-700, #475569);
}

.overview-badge--on {
    background: rgba(5, 150, 105, 0.12);
    color: var(--puc-green, #059669);
}

.overview-badge--off {
    background: rgba(148, 163, 184, 0.13);
    color: var(--puc-slate-500, #64748b);
}

.overview-badge--link {
    background: rgba(0, 123, 255, 0.09);
    color: var(--puc-blue-strong, #2563eb);
}

.overview-badge--empty {
    background: rgba(241, 245, 249, 0.94);
    color: var(--puc-slate-500, #64748b);
}

.detail-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    border-radius: 12px;
    border: 1px solid rgba(226, 232, 240, 0.92);
    background: rgba(255, 255, 255, 0.95);
}

.detail-section-head {
    display: flex;
    flex-direction: column;
}

.detail-section-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--puc-navy, #1b263b);
}

.detail-section--status {
    padding-bottom: 8px;
}

.field-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.field-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.field-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    color: var(--puc-slate-700, #475569);
    letter-spacing: 0.04em;
}

.req {
    color: var(--puc-red, #f43f5e);
}

.field-input {
    border: 1px solid rgba(226, 232, 240, 0.96);
    border-radius: 9px;
    padding: 8px 10px;
    font-size: 12px;
    color: var(--puc-navy, #1b263b);
    outline: none;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease,
        background 0.18s ease;
    background: rgba(255, 255, 255, 0.96);
}

.field-input:focus {
    border-color: rgba(37, 99, 235, 0.7);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.field-input--disabled {
    background: rgba(248, 250, 252, 0.95);
    color: var(--puc-slate-500, #64748b);
    cursor: default;
}

.field-input--error {
    border-color: rgba(244, 63, 94, 0.72);
}

.field-error {
    margin: 0;
    font-size: 10px;
    color: var(--puc-red, #f43f5e);
}

.detail-basic-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 12px;
    align-items: start;
}

@media (max-width: 520px) {
    .detail-basic-grid {
        grid-template-columns: 1fr;
    }
}

.field-row--remarks {
    margin-top: 8px;
}

.field-textarea {
    border: 1px solid rgba(226, 232, 240, 0.96);
    border-radius: 9px;
    padding: 8px 10px;
    font-size: 12px;
    color: var(--puc-navy, #1b263b);
    outline: none;
    background: rgba(255, 255, 255, 0.96);
    font-family: inherit;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease;
}

.field-textarea--fixed {
    height: 76px;
    min-height: 76px;
    max-height: 76px;
    resize: none;
    overflow-y: auto;
    box-sizing: border-box;
}

.field-textarea:focus {
    border-color: rgba(37, 99, 235, 0.7);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.detail-section--energy .detail-energy-hint,
.detail-section--energy .detail-energy-desc,
.detail-section--energy .detail-energy-empty {
    margin: 0;
    font-size: 11px;
    line-height: 1.45;
    color: var(--puc-slate-500, #64748b);
}

.detail-energy-hint {
    color: var(--puc-slate-600, #475569);
}

.detail-energy-desc {
    margin-bottom: 8px;
}

.detail-energy-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
}

.detail-energy-empty--inline {
    margin: 0;
}

.detail-energy-btn {
    min-height: 30px;
    padding: 5px 10px;
    border-radius: 8px;
    border: 1px solid rgba(226, 232, 240, 0.96);
    background: #fff;
    font-size: 11px;
    font-weight: 700;
    color: var(--puc-slate-700, #475569);
    cursor: pointer;
}

.detail-energy-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.detail-energy-chips {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
}

.detail-energy-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    padding: 3px 6px 3px 8px;
    border-radius: 999px;
    border: 1px solid rgba(226, 232, 240, 0.96);
    background: #fff;
    font-size: 11px;
    color: var(--puc-slate-700, #475569);
}

.detail-energy-chip-txt {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
}

.detail-energy-chip-x {
    border: none;
    background: transparent;
    padding: 2px;
    cursor: pointer;
    color: var(--puc-slate-400, #94a3b8);
    line-height: 1;
}

.detail-energy-add {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    min-height: 26px;
    padding: 3px 10px 3px 8px;
    border-radius: 999px;
    border: 1px dashed rgba(148, 163, 184, 0.55);
    background: rgba(255, 255, 255, 0.96);
    font-size: 11px;
    font-weight: 700;
    color: var(--puc-slate-600, #475569);
    cursor: pointer;
    transition:
        border-color 0.15s ease,
        background 0.15s ease,
        color 0.15s ease;
}

.detail-energy-add:hover:not(:disabled) {
    border-color: rgba(37, 99, 235, 0.35);
    color: var(--puc-blue-strong, #2563eb);
    background: rgba(239, 246, 255, 0.65);
}

.detail-energy-add:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.detail-energy-add i {
    font-size: 10px;
    color: var(--puc-slate-400, #94a3b8);
}

.detail-energy-add:hover:not(:disabled) i {
    color: var(--puc-blue-strong, #2563eb);
}

.unit-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(72px, 1fr));
    gap: 6px;
}

.unit-grid--disabled .unit-chip {
    cursor: default;
    opacity: 0.72;
}

.unit-chip {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 46px;
    padding: 8px 6px;
    gap: 3px;
    border: 1px solid rgba(226, 232, 240, 0.96);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.98);
    cursor: pointer;
    transition:
        border-color 0.18s ease,
        transform 0.18s ease,
        box-shadow 0.18s ease,
        background 0.18s ease;
}

.unit-chip:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: rgba(37, 99, 235, 0.4);
    box-shadow: 0 10px 20px rgba(37, 99, 235, 0.08);
}

.unit-chip--on {
    border-color: rgba(37, 99, 235, 0.42);
    background: rgba(0, 123, 255, 0.08);
}

.unit-chip b {
    font-size: 12px;
    font-weight: 700;
    color: var(--puc-slate-700, #475569);
}

.unit-chip--on b {
    color: var(--puc-blue-strong, #2563eb);
}

.unit-chip small {
    font-size: 10px;
    color: var(--puc-slate-400, #94a3b8);
}

.pt-selector {
    border: 1px solid rgba(226, 232, 240, 0.96);
    border-radius: 10px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.96);
}

.pt-selector--disabled .pt-display {
    cursor: default;
}

.pt-display {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 10px;
    cursor: pointer;
    transition: background 0.18s ease;
}

.pt-display:hover {
    background: rgba(248, 250, 252, 0.88);
}

.pt-icon {
    font-size: 13px;
    flex-shrink: 0;
}

.pt-icon--linked {
    color: var(--puc-green, #059669);
}

.pt-icon--empty {
    color: var(--puc-slate-300, #cbd5e1);
}

.pt-copy {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}

.pt-addr {
    font-size: 12px;
    color: var(--puc-slate-500, #64748b);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pt-addr--empty {
    color: var(--puc-slate-400, #94a3b8);
}

.pt-clear {
    box-sizing: border-box;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: rgba(148, 163, 184, 0.14);
    color: var(--puc-slate-500, #64748b);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition:
        background 0.18s ease,
        color 0.18s ease;
}

.pt-clear:hover {
    background: rgba(244, 63, 94, 0.12);
    color: var(--puc-red, #f43f5e);
}

.pt-search-ico {
    font-size: 12px;
    color: var(--puc-slate-300, #cbd5e1);
    flex-shrink: 0;
}

.yn-toggle {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
}

.yn-btn {
    box-sizing: border-box;
    min-height: 34px;
    padding: 8px 10px;
    border: 1px solid rgba(226, 232, 240, 0.96);
    border-radius: 12px;
    background: rgba(248, 250, 252, 0.86);
    color: var(--puc-slate-500, #64748b);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition:
        border-color 0.18s ease,
        background 0.18s ease,
        color 0.18s ease;
}

.yn-toggle--disabled .yn-btn {
    cursor: default;
}

.yn-btn--on {
    border-color: rgba(5, 150, 105, 0.3);
    background: rgba(5, 150, 105, 0.1);
    color: var(--puc-green, #059669);
}

.yn-btn--off {
    border-color: rgba(148, 163, 184, 0.28);
    background: rgba(148, 163, 184, 0.12);
    color: var(--puc-slate-700, #475569);
}

@media (max-width: 1024px) {
    .detail-grid {
        grid-template-columns: 1fr;
    }
}
</style>
